<template>
  <div>
    <div v-if="!$route.meta.hideCreditHeader">
      <h6
        class="text-uppercase font-weight-bold"
        :class="(baseRoute === 'mobile' || isMobileScreenSize) && shouldShowProgressBar ? 'd-none' : 'd-md-block'"
      >
        {{ wrapperTitle }}
      </h6>
      <div class="custom-mb">
        <div :class="$route.meta.parentSection ? 'custom-bar' : ''" v-if="shouldShowProgressBar">
          <ProgressBar
            :routes="creditRoutes"
            :filterFn="progressBarFilterFunction"
            :labelsFn="progressBarLabelsFunction"
          />
        </div>
        <MobialisHorizontalRuleHeader v-else> </MobialisHorizontalRuleHeader>
        <div v-if="displayVehicleName && displayVehicleName !== ''" class="font-weight-bold mt-3">
          {{ displayVehicleName }}
        </div>
      </div>
    </div>
    <router-view name="hero"></router-view>
    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperFunctions from '@/utils/helperFunctions';
import creditDataSourceCA from '@/utils/enums/creditDataSourceCA';
import TradeInSource from '@/utils/enums/tradeInSource';
import ProgressBar from '@/views/shared/progress-bar/ProgressBar.vue';
import desktopCreditRoutes from '@/router/desktop/credit';
import mobileCreditRoutes from '@/router/mobile/credit';
import embedCreditRoutes from '@/router/embed/credit';

export default {
  name: 'CreditWrapper',
  components: {
    ProgressBar,
  },
  computed: {
    ...mapGetters('business', [
      'isPrequalificationEnabled',
      'prequalificationTradeInEquityAssessmentEnabled',
      'prequalificationIncomeAndDebtEnabled',
      'showSiriusXMBanner',
      'prequalificationEnhancedEquifaxTradeInEquityAssessmentEnabled',
      'getDefaultCreditDataSourceCA',
    ]),
    tradeInSourceIsCarfax() {
      return this.$store.getters['business/getTradeInSource'] === TradeInSource.CARFAX;
    },
    wrapperTitle() {
      if (helperFunctions.isAVFinanceFlow(this.$route.path)) {
        return this.$t('message.credit.financeApplication');
      }
      if (this.isPrequalificationEnabled) {
        return this.$t('message.credit.application.preQualification.creditWrapperTitle');
      }
      if (!this.isPrequalificationEnabled) {
        if (this.getDefaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX) {
          return this.$t('message.credit.credit');
        }
        if (this.getDefaultCreditDataSourceCA === creditDataSourceCA.EQUIFAX_ID_AUTH_NEIGHBOURHOOD_CREDIT) {
          return this.$t('message.credit.creditCheck');
        }
      }
      return this.$t('message.credit.creditScore');
    },
    displayVehicleName() {
      if ((this.$route.meta && !this.$route.meta.displayVehicleNameHeader) || this.showSiriusXMBanner) {
        return '';
      }
      const vehicle = this.$store.state.credit.CIMFinanceApplicationVehicle
        ? this.$store.state.credit.CIMFinanceApplicationVehicle.vehicle
        : this.$store.state.vehicle.vehicleInfo && this.$store.state.vehicle.vehicleInfo.year
        ? this.$store.state.vehicle.vehicleInfo
        : null;
      return vehicle ? helperFunctions.getVehicleName(vehicle, ['year', 'make', 'model', 'trim']) : '';
    },

    // Returns a filter function for the progress bar steps
    progressBarFilterFunction() {
      const preqIncomeDebt = this.prequalificationIncomeAndDebtEnabled;
      const preqTradeIn = this.prequalificationTradeInEquityAssessmentEnabled;
      const ssnEnabled = this.$store.state.business.info.creditSettings.enableSSNCapture;
      const isReturnToFinanceApp = this.$route.query && !!this.$route.query.returnToFinanceApp;

      // Ex. Credit Route with Childrens
      const currentRoute = this.creditRoutes.find(
        r => r.children && r.children.some(child => child.name === this.$route.name)
      );

      return route => {
        if (!route || !route.name) return false;
        const { name } = route;
        let forbiddenSections = [];
        //  no tradeIn
        if (!preqTradeIn) {
          forbiddenSections = ['tradeIn'];
        }
        //  credit only
        if (!preqIncomeDebt && !preqTradeIn) {
          forbiddenSections = ['incomeAndDebt', 'tradeIn'];
        }
        // if US show only 2 steps
        if (this.isUSRoute) {
          forbiddenSections = ['idVerification', 'incomeAndDebt', 'tradeIn'];
        }
        const forbiddenRoutes = currentRoute.children
          .filter(child => forbiddenSections.includes(child.meta?.parentSection))
          .map(child => child.name);

        if (helperFunctions.isAVFinanceFlow(this.$route.path)) {
          let allowedPaths = ['employer-form', 'mortgage-form', 'thank-you'];
          if (isReturnToFinanceApp) {
            // Insert mobile pages if returning to finance app
            allowedPaths.push('mobile-code-sender', 'returning-customer-mobile-verification');
          }
          if (ssnEnabled) {
            // Insert 'social-security' page if SSN capture is enabled
            allowedPaths.push('social-security');
          }
          allowedPaths = allowedPaths.map(item => `${item}-${this.baseRoute}`);

          return allowedPaths.includes(name);
        }
        return !forbiddenRoutes.includes(name);
      };
    },

    // Determines if the progress bar should be shown based on the current route meta (progressBarType)
    shouldShowProgressBar() {
      const currentRoute = this.creditRoutes.find(
        r => r.children && r.children.some(child => child.name === this.$route.name)
      );

      return !!(currentRoute && currentRoute.meta && currentRoute.meta.progressBarType);
    },

    // Returns the correct credit routes based on the current route path (mobile, desktop, or embed)
    creditRoutes() {
      return this.baseRoute === 'mobile'
        ? mobileCreditRoutes
        : this.baseRoute === 'desktop'
        ? desktopCreditRoutes
        : this.baseRoute === 'embed'
        ? embedCreditRoutes
        : [];
    },

    // For the progressBar labels, we determine if we have custom label names for the parent sections
    progressBarLabelsFunction() {
      return labels => {
        const result = {};
        Object.entries(labels).forEach(([key]) => {
          // Assign translated label for the current key
          let translatedLabel = this.$t(`message.credit.progressBarSections.${key}`);

          // Special handling for the 'results' key
          if (key === 'results') {
            const preqIncomeDebt = this.prequalificationIncomeAndDebtEnabled;
            const preqTradeIn = this.prequalificationTradeInEquityAssessmentEnabled;
            if (preqIncomeDebt) {
              translatedLabel = this.$t('message.credit.progressBarSections.preQualification');
              if (preqTradeIn) {
                translatedLabel = this.$t('message.credit.progressBarSections.results');
              }
            } else {
              translatedLabel = this.$t('message.credit.progressBarSections.creditResults');
            }
          }
          result[key] = translatedLabel;
        });
        return result;
      };
    },
  },
  mounted() {
    // A hacky way to fix the weird IOS blank page issue
    // when a user comes from DHS, credit flow is blank (but DOM is existing!)
    // Force to re-render the app
    // Only for Mobile
    if (this.isAVRMobile) {
      document.body.style.height = 'initial';
      this.$nextTick(() =>
        setTimeout(() => {
          document.body.style.height = '';
        }, 10)
      );
    }
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';
.score-text {
  font-size: 14px;
  font-weight: bold;
  color: $primary;
}
.custom-mb {
  margin-bottom: 24px;
  .custom-bar {
    @media (max-width: 576px) {
      margin-left: -13px;
      margin-right: -12px;
    }
    margin-left: -16px;
    margin-right: -15px;
  }
}
</style>
